/* zoneDrawing.js

  These are functions specific to drawing operations with zones, e.g. merging, splitting, drop pivot. More general
  zone-related utils or functions shared beyond drawing can go in zoneOperations.js. */

import { cloneDeep, get, omit } from 'lodash';

import { drawingStates } from 'constants/values';
import ShapeType from 'mapping/leaflet/PropTypes/ShapeType';

// utils
// import { checkGeometryIntegrity } from 'geometry/integrity';
import { inheritZoneMetaFromMultiple } from 'util/zoneInheritance';
import { calculateUnion } from 'geometry/geojson/union';
import { contains } from 'geometry/geojson/contains';
import { calculateOverlap, doPolygonsIntersect } from 'geometry/geojson/intersect';
import { calculateDifference } from 'geometry/geojson/difference';
import { getFeature, multiPolygonToFeatureArray, hasGeometry } from 'geometry/geojson/convert';
import { copyZoneMeta, createNewZone, generateZoneId, getNextZoneIndex } from 'util/zoneCreation';
import { area, equal } from '3rd-party-wrappers/turf';
import { workerLogError } from 'scripts/editZones.worker';

// selectors
import { getZonesArrayWithIds } from 'scripts/zones/selectors';

const _getAdjacentZones = ({zones, zone}) => {
  const adjacentZones = [];
  Object.keys(zones).forEach(zoneId => {
    const eachZone = zones[zoneId];
    if (doPolygonsIntersect({polygon1:zone.geoJson, polygon2:eachZone.geoJson})) {
      adjacentZones.push(eachZone);
    }
  });
  return adjacentZones;
};

/**
 * Insert a new zone into zones using "intersect" mode. This mode will create a new zone within every other zone that
 * it intersects.
 *
 * @param zones Object with each member containing a zone.
 * @param newZoneGeoJson GeoJson for new zone to be inserted.
 * @param isCheckStrip Indicates if the zone should be tagged as a check strip.
 *
 * @returns A new zones object with copied values.
 */
const _insertZoneWithIntersection = ({ zones, newZoneGeoJson, library, isCheckStrip }) => {
  const newZones = {};

  Object.keys(zones).forEach(zoneId => {
    const zoneGeoJson = zones[zoneId].geoJson;
    const overlapGeometry = calculateOverlap(zoneGeoJson, newZoneGeoJson, library);

    if (!hasGeometry(overlapGeometry) // There is no intersection with this polygon.
        || equal(overlapGeometry, zoneGeoJson)) {
      // Intersection is equal to the polygon we're comparing. This means what we're adding is exactly equal or
      // bigger to one of our current polygons.
      newZones[zoneId] = cloneDeep(zones[zoneId]); // So just keep the zone as-is.
    } else { // Otherwise, add a new zone for the intersected geometry, and copy over the existing zone with intersection geometry cut out.
      const diffGeometry = calculateDifference(zoneGeoJson, newZoneGeoJson, library);

      if (diffGeometry === null) { // The new shape entirely encloses the zone.
        newZones[zoneId] = cloneDeep(zones[zoneId]); // Keep the zone as-is. It already represents the intersection of the new shape.
        return;
      }

      const intersectionZoneId = generateZoneId(newZones);
      newZones[intersectionZoneId] = createNewZone({
        geoJson:getFeature(overlapGeometry),
        zoneId:intersectionZoneId,
        isCheckStrip
      });
      newZones[zoneId] = cloneDeep(zones[zoneId]);
      newZones[zoneId].geoJson = getFeature(diffGeometry);
    }
  });

  return newZones;
};

/**
 * Insert a new zone into zones using "overwrite" mode. This mode cuts holes from any existing zones that overlap the
 * new zone, without creating new zones for intersections.
 *
 * @param zones Object with each member containing a zone.
 * @param newZoneGeoJson GeoJson for new zone to be inserted.
 * @param isCheckStrip Indicates if the zone should be tagged as a check strip.
 *
 * @returns A new zones object with copied values.
 */
const _insertZoneWithOverwrite = ({ zones, newZoneGeoJson, library, isCheckStrip }) => {
  const KEEP_THRESHOLD = 0.1;
  const newZones = {};

  Object.keys(zones).forEach(zoneId => {
    const zoneGeoJson = zones[zoneId].geoJson;
    const overlapGeometry = calculateOverlap(zoneGeoJson, newZoneGeoJson, library);

    if (!hasGeometry(overlapGeometry)) { // If there is no intersection, then the zone is copied over as-is.
      newZones[zoneId] = cloneDeep(zones[zoneId]);
    } else if (!equal(zoneGeoJson, overlapGeometry)) { // Some portion of the existing zone is outside of the inserted zone. (otherwise it is completely overwritten)
      // Add the remainder (non-intersected portion) of the current zone. Unless it's too tiny to worry about.
      const diff = calculateDifference(zoneGeoJson, newZoneGeoJson, library);
      if (diff && area(diff) >= KEEP_THRESHOLD) {
        newZones[zoneId] = cloneDeep(zones[zoneId]);
        newZones[zoneId].geoJson = getFeature(diff);
      }
    }
  });

  // Add the new zone.
  const newZoneId = generateZoneId(newZones);
  newZones[newZoneId] = createNewZone({ geoJson: getFeature(newZoneGeoJson), zoneId:newZoneId, isCheckStrip });

  return newZones;
};

/** Insert a zone into a collection of zones, performing geometric operations needed. This is copied over from
 *  geotools (Long live Lord Konstantine!) and modified to be geometric-only (no operations on .properties/zone
 *  meta data).
 *
 *  @param zones              Zones into which new zone will be inserted.
 *  @param newZoneGeoJson     Geometry of new zone to insert. Any .properties are discarded.
 *  @param addAsIntersection  If true, then new zone may be divided so that it contains a separate zones for each
 *                            existing zone it overlaps. If false, ("overwrite" mode) then the new zone will just be
 *                            one zone, regardless of existing zones it may overlap.
 *  @param isCheckStrip       If true, the new zone will be tagged as a check strip in its metadata.
 *  @return A new zones object with copied values.
 */
const _insertZone = ({ zones, newZoneGeoJson, addAsIntersection, boundaries, isCheckStrip }) => {

  // Clip new zone to a shape that fits within passed field boundaries
  const library = ['MFOGEL'];
  const newZoneClippedGeometry = calculateOverlap(boundaries, newZoneGeoJson, library);
  if (!hasGeometry(newZoneClippedGeometry)) { // New zone is entirely outside field, so just return copy of zones as-is.
    return cloneDeep(zones);
  }

  // Insert new zone via either overwriting or intersectng.
  const newZones = addAsIntersection
    ? _insertZoneWithIntersection({ zones, newZoneGeoJson:newZoneClippedGeometry, library, isCheckStrip })
    : _insertZoneWithOverwrite({ zones, newZoneGeoJson:newZoneClippedGeometry, library, isCheckStrip });

  return newZones;
};

/* TODO: Bring back if want to use checkGeometryIntegrity()
const _verifyGeometryIntegrityForZones = ({zones, logDescription}) => {
  // For now, I think this works fine. Future GI rules may need the tentative script to be
  // better populated. When that happens, those errors should be really obvious to spot when
  // an exception is thrown in checkGeometryIntegrity.
  const script = { zones };

  // TODO - Will I need to pass boundaries too for gap detection?
  return (checkGeometryIntegrity({script, logDescription}) === null);
};
*/

const _calcZonesForAddShape = ({zones, newZoneGeoJson, addAsIntersection, scriptType, boundaries, isCheckStrip }) => {
  const logDescription = 'adding zone(s) from drawing';
  try {
    const isNewZone = (zone) => zone.meta.index === undefined;

    // Get new set of zones reflecting geometric changes.
    const retZones = _insertZone({ zones, newZoneGeoJson, addAsIntersection, boundaries, isCheckStrip });

    /* Look for newly created zones that are entirely inside of one of the old zones. This means they were cut out of
       the old zone. For these, add a hint that will later allow correct inheritance of properties. */
    Object.keys(retZones).forEach(retZoneId => {
      const retZone = retZones[retZoneId];
      if (isNewZone(retZone)) {
        Object.keys(zones).some(oldZoneId => {
          const oldZone = zones[oldZoneId];
          if (contains(oldZone.geoJson, retZone.geoJson)) {
            retZone.cutEntirelyFrom = oldZone; // assigned temporarily for use in this function and then deleted below.
            return true;
          }
          return false;
        });
      }
    });

    // Pass through zones again and fix outdated/missing data.
    let zoneIndex = getNextZoneIndex(zones);
    Object.keys(retZones).forEach(retZoneId => {
      const retZone = retZones[retZoneId];
      if (isNewZone(retZone)) {
        if (retZone.cutEntirelyFrom) {
          copyZoneMeta(retZone, retZone.cutEntirelyFrom);
          delete retZone.cutEntirelyFrom;
        } else {
          const adjacentZones = _getAdjacentZones({ zones, zone:retZone });
          inheritZoneMetaFromMultiple({toZone:retZone, fromZones:adjacentZones, scriptType});
        }
        retZone.meta.index = zoneIndex++;
      }
    });

    /* TODO: Bring back if want to use checkGeometryIntegrity()
    if (!_verifyGeometryIntegrityForZones({zones:retZones, logDescription}))
    { return null; }
    */

    return retZones;
  } catch(error) {
    workerLogError({message:`Exception while ${logDescription}`, error});
    return null;
  }
};

export const isZoneSplittable = (zone) => get(zone, 'geoJson.geometry.type')==='MultiPolygon';
export const zoneIsSelected = (selectedZoneIds, zone) => selectedZoneIds.includes(zone.meta.zoneId);
export const zoneIsDisabled = (drawingState, zone) =>
  drawingState === drawingStates.SPLIT_ZONE_SELECTION ? !isZoneSplittable(zone) : false;

export const calcZonesForSplit = ({zones, splitZoneId}) => {
  const logDescription = 'splitting zone';
  try {
    const zoneToSplit = zones[splitZoneId];
    if (!isZoneSplittable(zoneToSplit)) { return zones; } // There is nothing to do.

    let zoneIndex = getNextZoneIndex(zones);
    const newZones = omit(zones, [splitZoneId]);
    const splitZoneFeatures = multiPolygonToFeatureArray(zoneToSplit.geoJson);
    splitZoneFeatures.forEach((geoJson) => {
      const zoneId = generateZoneId(zones);
      const splitZone = createNewZone({ zoneId, geoJson, isCheckStrip: false });
      copyZoneMeta(splitZone, zoneToSplit);
      splitZone.meta.index = zoneIndex++;
      newZones[zoneId] = splitZone;
    });

    /* TODO: Bring back if want to use checkGeometryIntegrity()
    if (!_verifyGeometryIntegrityForZones({zones:newZones, logDescription}))
    { return null; }
    */

    return newZones;
  } catch(error) {
    workerLogError({message:`Exception while ${logDescription}`, error});
    return null;
  }
};

export const calcZonesForMerge = ({zones, mergeZoneIds, scriptType}) => {
  const logDescription = 'merging zones';
  try {
    if (!mergeZoneIds || mergeZoneIds.length < 2) { return zones; } // There is nothing to do.

    const newZones = omit(zones, [...mergeZoneIds]);
    const zonesToMerge = getZonesArrayWithIds({zones, zoneIds:mergeZoneIds});
    const zonesToMergeGeoJson = zonesToMerge.map(zone => zone.geoJson);
    const geoJson = calculateUnion({geoJsons:zonesToMergeGeoJson, useLibraries: ['MFOGEL'] });

    const zoneIndex = getNextZoneIndex(zones);
    const zoneId = generateZoneId(zones);
    const mergedZone = createNewZone({ zoneId, geoJson, isCheckStrip: false });
    inheritZoneMetaFromMultiple({toZone:mergedZone, fromZones:zonesToMerge, scriptType});
    mergedZone.meta.index = zoneIndex;
    newZones[zoneId] = mergedZone;

    /* TODO: Bring back if want to use checkGeometryIntegrity()
    if (!_verifyGeometryIntegrityForZones({zones:newZones, logDescription}))
    { return null; }
    */

    return newZones;
  } catch(error) {
    workerLogError({message:`Exception while ${logDescription}`, error});
    return null;
  }
};

export const calcZonesForAddPivot = ({zones, newZoneGeoJson, scriptType, boundaries }) => {
  return _calcZonesForAddShape({zones, newZoneGeoJson, addAsIntersection:true, scriptType, boundaries });
};

export const calcZonesForAddSquare = ({zones, newZoneGeoJson, scriptType, boundaries, isCheckStrip }) => {
  return _calcZonesForAddShape({zones, newZoneGeoJson, addAsIntersection:false, scriptType, boundaries, isCheckStrip });
};

export const calcZonesForAddPolygon = ({zones, newZoneGeoJson, scriptType, boundaries, isCheckStrip }) => {
  return _calcZonesForAddShape({zones, newZoneGeoJson, addAsIntersection:false, scriptType, boundaries, isCheckStrip });
};

/** @return the first drawing state to use for creating a type of shape. */
export const getDrawingStateForCreateShape = (shapeType) => {
  switch (shapeType) {
    case ShapeType.CIRCLE: return drawingStates.DRAWING_PIVOT;
    case ShapeType.POLYGON: return drawingStates.DRAWING_POLYGON;
    case ShapeType.SQUARE: return drawingStates.DRAWING_SQUARE;
    default: return drawingStates.NONE;
  }
};

/**
 * @param {string} drawingState
 * @return {string} Map drawing state that corresponds to zone drawing state.
 * TODO - revisit these two sets of states when adding more drawing states later. Design goals:
 *    - mapping/leaflet drawing states remain unware of business logic concepts like zones, merging, splitting.
 *    - zone drawing state retains ability to express business logic concepts
 *    - mapping/leaflet drawing states express more granularity, e.g. has user started drawing a circle yet,
 *      completed drawing?
 *    - consolidate the 2 sets of drawing states or change them in a useful way so they don't just become a 1-to-1
 *      mirror of each other like now
 */
export const zoneDrawingStateToMapDrawState = (drawingState) => {
  switch (drawingState) {
    case drawingStates.DRAWING_PIVOT: return ShapeType.CIRCLE;
    case drawingStates.DRAWING_POLYGON: return ShapeType.POLYGON;
    case drawingStates.DRAWING_SQUARE: return ShapeType.SQUARE;
    default: return ShapeType.NONE;
  }
};

export const unitTestPrivate = {
  _calcZonesForAddShape,
  _getAdjacentZones,
  _insertZone,
  _insertZoneWithIntersection,
  _insertZoneWithOverwrite
};
